import { useRef, useCallback, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import { BOOK_CALL_URL, YOUTUBE_CHANNEL_URL, SUBSTACK_URL, DISCORD_INVITE_URL } from "@/lib/links";
import { useTheme } from "@/components/theme/ThemeProvider";
import { getPalette, type ThemePalette } from "@/lib/themePalette";

// ─── Word definitions ─────────────────────────────────────────────────────────
const WORDS = [
  { text: "Turn",      accent: false, line: 0 },
  { text: "manual",    accent: false, line: 0 },
  { text: "workflows", accent: true,  line: 0 },
  { text: "into",      accent: false, line: 1 },
  { text: "reliable",  accent: false, line: 1 },
  { text: "AI",        accent: true,  line: 1 },
  { text: "systems.",  accent: false, line: 1 },
];

// Synaptic connection strengths between word pairs
const SYNAPSES: Record<string, number> = {
  "workflows-AI":        0.95,
  "workflows-systems.":  0.9,
  "AI-systems.":         0.9,
  "manual-reliable":     0.85,
  "manual-workflows":    0.8,
  "reliable-AI":         0.75,
  "Turn-into":           0.7,
  "into-reliable":       0.6,
  "Turn-manual":         0.55,
  "workflows-into":      0.5,
  "Turn-workflows":      0.45,
  "reliable-systems.":   0.85,
};

function getSynapseStrength(a: string, b: string): number {
  return SYNAPSES[`${a}-${b}`] ?? SYNAPSES[`${b}-${a}`] ?? 0.12;
}

// ─── Particle type ────────────────────────────────────────────────────────────
interface Particle {
  id: number;
  x: number; y: number;
  vx: number; vy: number;
  radius: number;
  accent: boolean;
  bridging: boolean;
  targetX: number; targetY: number;
  trail: { x: number; y: number }[];
  opacity: number;
  life: number;
}

// ─── Synapse pulse type ───────────────────────────────────────────────────────
interface SynapsePulse {
  id: number;
  fromIdx: number;
  toIdx: number;
  t: number; // 0→1 progress along the synapse
  speed: number;
  accent: boolean;
}

// ─── Neural + Particle Canvas ─────────────────────────────────────────────────
function NeuralBridgeCanvas({
  width, height,
  mouseRef,
  wordRectsRef,
  firedWord,
  palette,
}: {
  width: number; height: number;
  mouseRef: React.MutableRefObject<{ x: number; y: number }>;
  wordRectsRef: React.MutableRefObject<DOMRect[]>;
  firedWord: number | null;
  palette: ThemePalette;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Keep the latest palette in a ref so the rAF draw loop reads current colors.
  const paletteRef = useRef(palette);
  paletteRef.current = palette;
  const stateRef = useRef<{
    particles: Particle[];
    pulses: SynapsePulse[];
    firedWord: number | null;
    idCounter: number;
    pulseTimer: number;
    spawnTimer: number;
    // synapse line opacities (animated)
    synapseOpacities: number[];
  }>({
    particles: [],
    pulses: [],
    firedWord: null,
    idCounter: 0,
    pulseTimer: 0,
    spawnTimer: 0,
    synapseOpacities: [],
  });

  useEffect(() => { stateRef.current.firedWord = firedWord; }, [firedWord]);

  // Spawn ambient particles
  useEffect(() => {
    if (width === 0 || height === 0) return;
    stateRef.current.particles = Array.from({ length: 44 }, (_, i) => ({
      id: i,
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      radius: 0.8 + Math.random() * 1.8,
      accent: i < 9,
      bridging: false,
      targetX: 0, targetY: 0,
      trail: [],
      opacity: 0.12 + Math.random() * 0.28,
      life: Math.random(),
    }));
  }, [width, height]);

  // Build edge list
  const edgesRef = useRef<{ a: number; b: number; strength: number }[]>([]);
  useEffect(() => {
    const edges: { a: number; b: number; strength: number }[] = [];
    for (let i = 0; i < WORDS.length; i++) {
      for (let j = i + 1; j < WORDS.length; j++) {
        const s = getSynapseStrength(WORDS[i].text, WORDS[j].text);
        if (s > 0.1) edges.push({ a: i, b: j, strength: s });
      }
    }
    edgesRef.current = edges;
    stateRef.current.synapseOpacities = edges.map(e => e.strength * 0.18);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || width === 0 || height === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf: number;

    const getWordCenter = (rect: DOMRect) => ({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const s = stateRef.current;
      // Theme-aware colors, read fresh each frame so a toggle recolors live.
      const pal = paletteRef.current;
      const ACCENT = pal.accent;   // acid green triplet
      const NEUTRAL = pal.neutral; // ambient/neutral triplet
      const PULSE = pal.pulse;     // brighter pulse/node triplet
      const BG = pal.bg;           // page bg triplet, for fade-to-bg stops
      const { firedWord } = s;
      const { x: mx, y: my } = mouseRef.current;
      const wordRects = wordRectsRef.current;
      const hasMouse = mx > 0 && mx < width && my > 0 && my < height;
      const wordCenters = wordRects.map(getWordCenter);
      const edges = edgesRef.current;

      s.pulseTimer++;
      s.spawnTimer++;

      // ── Spawn synaptic pulses periodically ──
      if (s.pulseTimer % 18 === 0 && edges.length > 0 && wordCenters.length > 0) {
        // Pick a random edge weighted by strength
        const totalW = edges.reduce((acc, e) => acc + e.strength, 0);
        let r = Math.random() * totalW;
        let chosen = edges[0];
        for (const e of edges) {
          r -= e.strength;
          if (r <= 0) { chosen = e; break; }
        }
        const isAccent = WORDS[chosen.a].accent || WORDS[chosen.b].accent;
        s.pulses.push({
          id: s.idCounter++,
          fromIdx: chosen.a,
          toIdx: chosen.b,
          t: 0,
          speed: 0.008 + Math.random() * 0.006,
          accent: isAccent,
        });
        // Occasionally fire reverse
        if (Math.random() < 0.4) {
          s.pulses.push({
            id: s.idCounter++,
            fromIdx: chosen.b,
            toIdx: chosen.a,
            t: 0,
            speed: 0.007 + Math.random() * 0.005,
            accent: isAccent,
          });
        }
      }

      // Extra pulses when a word fires
      if (firedWord !== null && s.pulseTimer % 4 === 0) {
        edges.forEach(e => {
          if ((e.a === firedWord || e.b === firedWord) && Math.random() < 0.6) {
            const from = e.a === firedWord ? e.a : e.b;
            const to   = e.a === firedWord ? e.b : e.a;
            s.pulses.push({
              id: s.idCounter++,
              fromIdx: from,
              toIdx: to,
              t: 0,
              speed: 0.014 + Math.random() * 0.008,
              accent: WORDS[from].accent || WORDS[to].accent,
            });
          }
        });
      }

      // ── Draw synapse lines ──
      edges.forEach((edge, ei) => {
        const ca = wordCenters[edge.a];
        const cb = wordCenters[edge.b];
        if (!ca || !cb) return;

        let targetOp = edge.strength * 0.14;
        if (firedWord !== null && (edge.a === firedWord || edge.b === firedWord)) {
          targetOp = edge.strength * 0.55;
        } else if (hasMouse) {
          const nearA = Math.hypot(ca.x - mx, ca.y - my) < 200;
          const nearB = Math.hypot(cb.x - mx, cb.y - my) < 200;
          if (nearA || nearB) targetOp = edge.strength * 0.38;
        }

        const cur = s.synapseOpacities[ei] ?? targetOp;
        s.synapseOpacities[ei] = cur + (targetOp - cur) * 0.07;
        const op = s.synapseOpacities[ei];
        if (op < 0.008) return;

        const isAccentEdge = (WORDS[edge.a].accent || WORDS[edge.b].accent) && edge.strength > 0.6;

        ctx.beginPath();
        ctx.moveTo(ca.x, ca.y);
        ctx.lineTo(cb.x, cb.y);
        ctx.strokeStyle = isAccentEdge
          ? `rgba(${ACCENT},${op})`
          : `rgba(${NEUTRAL},${op * 0.65})`;
        ctx.lineWidth = edge.strength * 0.9;
        ctx.stroke();
      });

      // ── Draw & update synapse pulses ──
      for (let i = s.pulses.length - 1; i >= 0; i--) {
        const pulse = s.pulses[i];
        const ca = wordCenters[pulse.fromIdx];
        const cb = wordCenters[pulse.toIdx];
        if (!ca || !cb) { s.pulses.splice(i, 1); continue; }

        pulse.t += pulse.speed;
        if (pulse.t >= 1) { s.pulses.splice(i, 1); continue; }

        const px = ca.x + (cb.x - ca.x) * pulse.t;
        const py = ca.y + (cb.y - ca.y) * pulse.t;

        // Glow
        if (pulse.accent) {
          const g = ctx.createRadialGradient(px, py, 0, px, py, 7);
          g.addColorStop(0, `rgba(${ACCENT},0.7)`);
          g.addColorStop(1, `rgba(${ACCENT},0)`);
          ctx.beginPath();
          ctx.arc(px, py, 7, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(px, py, pulse.accent ? 2.2 : 1.5, 0, Math.PI * 2);
        ctx.fillStyle = pulse.accent
          ? `rgba(${ACCENT},0.9)`
          : `rgba(${PULSE},0.65)`;
        ctx.fill();
      }

      // ── Draw neuron nodes at word centers ──
      wordCenters.forEach((wc, wi) => {
        if (!wc) return;
        const isAccent = WORDS[wi].accent;
        const isFired = firedWord === wi;
        const nearMouse = hasMouse && Math.hypot(wc.x - mx, wc.y - my) < 180;

        const nodeR = isFired ? 5 : nearMouse ? 4 : 3;
        const nodeAlpha = isFired ? 0.9 : nearMouse ? 0.65 : 0.35;

        if (isFired || nearMouse) {
          // Outer glow ring
          const g = ctx.createRadialGradient(wc.x, wc.y, 0, wc.x, wc.y, nodeR * 5);
          g.addColorStop(0, isAccent ? `rgba(${ACCENT},${nodeAlpha * 0.4})` : `rgba(${PULSE},${nodeAlpha * 0.2})`);
          g.addColorStop(1, `rgba(${BG},0)`);
          ctx.beginPath();
          ctx.arc(wc.x, wc.y, nodeR * 5, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(wc.x, wc.y, nodeR, 0, Math.PI * 2);
        ctx.fillStyle = isAccent
          ? `rgba(${ACCENT},${nodeAlpha})`
          : `rgba(${PULSE},${nodeAlpha * 0.7})`;
        ctx.fill();
      });

      // ── Spawn bridge particles from cursor toward nearest word ──
      if (hasMouse && s.spawnTimer % 7 === 0 && wordCenters.length > 0) {
        let nearest = wordCenters[0];
        let nearDist = Infinity;
        wordCenters.forEach(wc => {
          if (!wc) return;
          const d = Math.hypot(wc.x - mx, wc.y - my);
          if (d < nearDist) { nearDist = d; nearest = wc; }
        });

        if (nearDist < 480) {
          const angle = Math.atan2(nearest.y - my, nearest.x - mx);
          const spread = (Math.random() - 0.5) * 0.55;
          const speed = 1.6 + Math.random() * 2.0;
          s.particles.push({
            id: s.idCounter++,
            x: mx + (Math.random() - 0.5) * 10,
            y: my + (Math.random() - 0.5) * 10,
            vx: Math.cos(angle + spread) * speed,
            vy: Math.sin(angle + spread) * speed,
            radius: 1.0 + Math.random() * 1.6,
            accent: Math.random() < 0.35,
            bridging: true,
            targetX: nearest.x, targetY: nearest.y,
            trail: [],
            opacity: 0.65 + Math.random() * 0.3,
            life: 1,
          });
        }

        // Return particles from words to cursor
        wordCenters.forEach(wc => {
          if (!wc) return;
          const d = Math.hypot(wc.x - mx, wc.y - my);
          if (d < 360 && Math.random() < 0.1) {
            const angle = Math.atan2(my - wc.y, mx - wc.x);
            const spread = (Math.random() - 0.5) * 0.45;
            const speed = 1.0 + Math.random() * 1.4;
            s.particles.push({
              id: s.idCounter++,
              x: wc.x + (Math.random() - 0.5) * 16,
              y: wc.y + (Math.random() - 0.5) * 16,
              vx: Math.cos(angle + spread) * speed,
              vy: Math.sin(angle + spread) * speed,
              radius: 0.7 + Math.random() * 1.2,
              accent: true,
              bridging: true,
              targetX: mx, targetY: my,
              trail: [],
              opacity: 0.45 + Math.random() * 0.3,
              life: 1,
            });
          }
        });
      }

      // ── Draw ambient particle connections ──
      for (let i = 0; i < s.particles.length; i++) {
        const a = s.particles[i];
        if (a.bridging) continue;
        for (let j = i + 1; j < s.particles.length; j++) {
          const b = s.particles[j];
          if (b.bridging) continue;
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 36) {
            const t = 1 - d / 36;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(${NEUTRAL},${t * 0.07 * Math.min(a.opacity, b.opacity)})`;
            ctx.lineWidth = 0.4;
            ctx.stroke();
          }
        }
      }

      // ── Cursor → word dashed bridge lines ──
      if (hasMouse) {
        wordCenters.forEach((wc, wi) => {
          if (!wc) return;
          const d = Math.hypot(wc.x - mx, wc.y - my);
          if (d < 400) {
            const t = Math.max(0, 1 - d / 400);
            const isAccent = WORDS[wi].accent;
            ctx.save();
            ctx.setLineDash([3, 8]);
            ctx.beginPath();
            ctx.moveTo(mx, my);
            ctx.lineTo(wc.x, wc.y);
            ctx.strokeStyle = isAccent
              ? `rgba(${ACCENT},${t * 0.2})`
              : `rgba(${NEUTRAL},${t * 0.09})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
            ctx.restore();
          }
        });
      }

      // ── Update & draw particles ──
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i];

        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 7) p.trail.shift();

        if (p.bridging) {
          const dx = p.targetX - p.x;
          const dy = p.targetY - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 16) {
            p.life -= 0.14;
          } else {
            p.vx += (dx / dist) * 0.055;
            p.vy += (dy / dist) * 0.055;
          }
          const spd = Math.hypot(p.vx, p.vy);
          if (spd > 3.2) { p.vx = (p.vx / spd) * 3.2; p.vy = (p.vy / spd) * 3.2; }
          p.life -= 0.007;
        } else {
          p.vx += (Math.random() - 0.5) * 0.018;
          p.vy += (Math.random() - 0.5) * 0.018;
          if (hasMouse) {
            const dx = p.x - mx;
            const dy = p.y - my;
            const d = Math.hypot(dx, dy);
            if (d < 80) {
              const f = (1 - d / 80) * 0.25;
              p.vx += (dx / d) * f;
              p.vy += (dy / d) * f;
            }
          }
          const spd = Math.hypot(p.vx, p.vy);
          if (spd > 0.7) { p.vx = (p.vx / spd) * 0.7; p.vy = (p.vy / spd) * 0.7; }
          if (p.x < 0) p.x = width;
          if (p.x > width) p.x = 0;
          if (p.y < 0) p.y = height;
          if (p.y > height) p.y = 0;
        }

        p.x += p.vx;
        p.y += p.vy;

        if (p.bridging && p.life <= 0) { s.particles.splice(i, 1); continue; }

        // Trail
        if (p.bridging && p.trail.length > 1) {
          for (let ti = 1; ti < p.trail.length; ti++) {
            const tt = ti / p.trail.length;
            ctx.beginPath();
            ctx.moveTo(p.trail[ti - 1].x, p.trail[ti - 1].y);
            ctx.lineTo(p.trail[ti].x, p.trail[ti].y);
            ctx.strokeStyle = p.accent
              ? `rgba(${ACCENT},${tt * p.opacity * p.life * 0.55})`
              : `rgba(${NEUTRAL},${tt * p.opacity * p.life * 0.3})`;
            ctx.lineWidth = p.radius * tt * 0.7;
            ctx.stroke();
          }
        }

        const alpha = p.bridging ? p.opacity * p.life : p.opacity;
        if (p.accent) {
          const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius * 2.5);
          g.addColorStop(0, `rgba(${ACCENT},${alpha * 0.7})`);
          g.addColorStop(1, `rgba(${ACCENT},0)`);
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.accent
          ? `rgba(${ACCENT},${alpha})`
          : `rgba(${NEUTRAL},${alpha * 0.65})`;
        ctx.fill();
      }

      // Cap particles
      if (s.particles.length > 180) s.particles.splice(0, s.particles.length - 180);

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
    // mouseRef/wordRectsRef are stable useRef objects from the parent; listing
    // them satisfies exhaustive-deps without ever re-triggering the effect.
  }, [width, height, mouseRef, wordRectsRef]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none"
      aria-hidden
    />
  );
}

// ─── Neural word ──────────────────────────────────────────────────────────────
function NeuralWord({
  word,
  index,
  onMount,
  onFire,
}: {
  word: (typeof WORDS)[number];
  index: number;
  onMount: (el: HTMLSpanElement | null) => void;
  onFire: (i: number | null) => void;
}) {
  const [fired, setFired] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    setFired(true);
    onFire(index);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setFired(false);
      onFire(null);
    }, 600);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <motion.span
      ref={onMount}
      className={`${word.accent ? "text-acid italic" : "text-chalk"} inline-block cursor-default select-none`}
      initial={{ opacity: 0, y: 44 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: fired ? 1.05 : 1,
        transition: {
          opacity: { delay: 0.3 + index * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
          y: { delay: 0.3 + index * 0.1, type: "spring", stiffness: 56, damping: 13, mass: 1.2 },
          scale: { type: "spring", stiffness: 400, damping: 20 },
        },
      }}
      style={{
        display: "inline-block",
        filter: fired
          ? word.accent
            ? "brightness(1.35) drop-shadow(0 0 14px rgba(47,107,79,0.7))"
            : "brightness(1.18) drop-shadow(0 0 10px rgba(180,200,190,0.4))"
          : "none",
        transition: "filter 0.15s",
      }}
      onMouseEnter={handleEnter}
    >
      {word.text}
    </motion.span>
  );
}

// ─── Main Hero ────────────────────────────────────────────────────────────────
export default function Hero() {
  const { theme } = useTheme();
  const palette = getPalette(theme);
  const sectionRef = useRef<HTMLDivElement>(null);
  const wordRefs   = useRef<(HTMLSpanElement | null)[]>([]);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const wordRectsRef = useRef<DOMRect[]>([]);
  const [dims,      setDims]      = useState({ w: 0, h: 0 });
  const [firedWord,    setFiredWord]    = useState<number | null>(null);

  // Measure section
  useEffect(() => {
    const measure = () => {
      if (!sectionRef.current) return;
      const r = sectionRef.current.getBoundingClientRect();
      setDims({ w: r.width, h: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const updateWordRects = useCallback(() => {
    if (!sectionRef.current) return;
    const sr = sectionRef.current.getBoundingClientRect();
    wordRectsRef.current = wordRefs.current
      .filter(Boolean)
      .map((el) => {
        const r = el!.getBoundingClientRect();
        return new DOMRect(r.left - sr.left, r.top - sr.top, r.width, r.height);
      });
  }, []);

  // Word rects relative to section
  useEffect(() => {
    let rafId = 0;
    let scrollTicking = false;
    const scheduleUpdate = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      rafId = window.requestAnimationFrame(() => {
        scrollTicking = false;
        updateWordRects();
      });
    };

    updateWordRects();
    const raf1 = window.requestAnimationFrame(updateWordRects);
    const raf2 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updateWordRects);
    });
    const t1 = window.setTimeout(updateWordRects, 350);
    const t2 = window.setTimeout(updateWordRects, 900);
    const t3 = window.setTimeout(updateWordRects, 1500);
    const ro = new ResizeObserver(updateWordRects);
    if (sectionRef.current) {
      ro.observe(sectionRef.current);
    }
    wordRefs.current.filter(Boolean).forEach((el) => {
      ro.observe(el!);
    });

    const fonts = document.fonts;
    let cancelled = false;
    if (fonts) {
      fonts.ready.then(() => {
        if (!cancelled) updateWordRects();
      });
    }

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      ro.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate);
    };
  }, [dims.w, dims.h, updateWordRects]);

  // Mouse relative to section
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!sectionRef.current) return;
      const r = sectionRef.current.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      mouseRef.current = { x, y };
    };
    const onLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
    };
    window.addEventListener("mousemove", onMove);
    sectionRef.current?.addEventListener("mouseleave", onLeave);
    const el = sectionRef.current;
    return () => {
      window.removeEventListener("mousemove", onMove);
      el?.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const fadeOut  = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  // Gentle parallax drift on the background image as the hero scrolls away.
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  // Dark mode is derived from the single warm light-master image via an ink
  // overlay (decided over generating a separate dark twin: the image is soft and
  // abstract, so a tint reads as "moody night" with zero composition drift).
  const isDark = theme === "dark";

  const scrollToApproach = useCallback(() => {
    document.querySelector("#manifesto")?.scrollIntoView({ behavior: "smooth" });
  }, []);


  const subItems = [
    { icon: "ri-youtube-line",     label: "Watch on YouTube",    href: YOUTUBE_CHANNEL_URL, external: true  },
    { icon: "ri-mail-line",        label: "Newsletter",          href: SUBSTACK_URL,          external: true },
    { icon: "ri-group-line",       label: "DOOing AI Collective", href: DISCORD_INVITE_URL, external: true },
    { icon: "ri-layout-grid-line", label: "Explore Projects",    href: "#projects",            external: false },
  ];

  const line0 = WORDS.filter((w) => w.line === 0);
  const line1 = WORDS.filter((w) => w.line === 1);
  const headlineFontSize = "clamp(1.5rem, 5.4vw, 5.5rem)";
  let wordIdx = 0;

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-screen bg-ink flex flex-col justify-between overflow-hidden"
    >
      {/* Background photo — a day/night twin: warm misty ridgelines (light mode)
          and an aurora-lit night of the same PNW scene (dark mode). They
          crossfade on theme toggle. */}
      <motion.div
        className="absolute inset-0 pointer-events-none select-none overflow-hidden"
        style={{ y: bgY }}
        aria-hidden
      >
        {/* Light: warm dawn ridgelines. Mobile biases the crop lower so the
            structured foreground carries the tall frame (center is empty sky). */}
        <img
          src="/hero/hero-ridgelines-1920.webp"
          srcSet="/hero/hero-ridgelines-1280.webp 1280w, /hero/hero-ridgelines-1920.webp 1920w"
          sizes="100vw"
          alt=""
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover [object-position:50%_72%] md:[object-position:50%_50%] transition-opacity duration-500"
          style={{ opacity: isDark ? 0 : 1 }}
        />
        {/* Dark: aurora night of the same scene. Already dark, so no ink tint. */}
        <img
          src="/hero/hero-aurora-1920.webp"
          srcSet="/hero/hero-aurora-1280.webp 1280w, /hero/hero-aurora-1920.webp 1920w"
          sizes="100vw"
          alt=""
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover [object-position:50%_60%] md:[object-position:50%_50%] transition-opacity duration-500"
          style={{ opacity: isDark ? 1 : 0 }}
        />
        {/* Top scrim for navbar + eyebrow contrast, fading out by ~45% down.
            Light mode: the bright dawn sky needs a gentle darkening. Dark mode:
            the aurora lives in the upper sky, so keep this light enough that the
            scrim protects the nav text without crushing the aurora. */}
        <div
          className="absolute inset-x-0 top-0 h-[45%] transition-opacity duration-500"
          style={{
            background:
              "linear-gradient(to bottom, rgb(var(--ink) / 0.85) 0%, transparent 100%)",
            opacity: isDark ? 0.4 : 0.32,
          }}
        />
        {/* Edge/corner vignette — keeps the center calm and frames the image. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 85% at 50% 42%, transparent 45%, rgb(var(--ink) / 0.28) 100%)",
          }}
        />
        {/* Legibility scrim behind the centered text column. Very light on the
            already-calm image; just enough to guarantee the headline reads. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(65% 50% at 50% 48%, rgb(var(--ink) / 0.18) 0%, transparent 72%)",
          }}
        />
        {/* Bottom fade into the page so the Hero hands off seamlessly to Stats. */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink" />
      </motion.div>

      {/* Neural + particle bridge canvas — dialed down so it reads as a subtle
          living layer over the photo rather than a competing background. */}
      {dims.w > 0 && (
        <div className="absolute inset-0 pointer-events-none opacity-50">
          <NeuralBridgeCanvas
            width={dims.w}
            height={dims.h}
            mouseRef={mouseRef}
            wordRectsRef={wordRectsRef}
            firedWord={firedWord}
            palette={palette}
          />
        </div>
      )}

      {/* Eyebrow */}
      <motion.div
        className="relative z-10 pt-28 px-6 lg:px-16 flex items-center justify-center gap-4 text-center"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="font-mono text-xs text-chalk-2 tracking-widest uppercase">DOO MADE</span>
        <span className="w-6 h-px bg-chalk-3/50" />
        <span className="font-mono text-xs text-chalk-2 tracking-widest uppercase">Clarity before implementation</span>
      </motion.div>

      {/* Main content */}
      <motion.div
        style={{ y: contentY, opacity: fadeOut }}
        className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 lg:px-16 py-12 text-center"
      >
        {/* Headline */}
        <div className="relative mb-10 max-w-6xl">
          <h1 className="font-display font-bold leading-[0.9] tracking-tightest overflow-visible">
            <div
              className="flex items-baseline justify-center gap-x-[0.16em] overflow-visible whitespace-nowrap md:gap-x-[0.22em]"
              style={{ fontSize: "clamp(1.65rem, 5.9vw, 6rem)" }}
            >
              {line0.map((word) => {
                const i = wordIdx++;
                return (
                  <NeuralWord
                    key={word.text}
                    word={word}
                    index={i}
                    onMount={(el) => { wordRefs.current[i] = el; }}
                    onFire={setFiredWord}
                  />
                );
              })}
            </div>
            <div
              className="mt-3 md:mt-4 flex items-baseline justify-center gap-x-[0.16em] overflow-visible whitespace-nowrap md:gap-x-[0.22em]"
              style={{ fontSize: headlineFontSize }}
            >
              {line1.map((word) => {
                const i = wordIdx++;
                return (
                  <NeuralWord
                    key={word.text}
                    word={word}
                    index={i}
                    onMount={(el) => { wordRefs.current[i] = el; }}
                    onFire={setFiredWord}
                  />
                );
              })}
            </div>
          </h1>
        </div>

        {/* Subhead */}
        <motion.p
          className="mt-6 w-full max-w-[28rem] px-2 text-[0.95rem] text-chalk-2 font-light leading-relaxed sm:max-w-[34rem] sm:text-base md:max-w-6xl lg:text-lg"
          style={{ textWrap: "balance" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          We find where AI creates real leverage, then build secure, production-ready systems around how your business actually runs.
          <span className="hidden md:block" />
          <span className="md:hidden"> </span>
          No generic tools. No vendor lock-in. No demo that dies in production.
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-acid text-white font-semibold text-sm tracking-tight rounded-sm hover:bg-acid-hover transition-colors duration-200 cursor-pointer whitespace-nowrap group"
          >
            Book an AI Workflow Fit Call
            <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform duration-200">→</span>
          </a>
          <button
            onClick={scrollToApproach}
            className="px-8 py-4 border border-ink-4 text-chalk-2 font-medium text-sm hover:border-acid-soft hover:text-chalk transition-all duration-200 cursor-pointer whitespace-nowrap rounded-sm"
          >
            Read the Approach
          </button>
        </motion.div>
      </motion.div>

      {/* Bottom strip */}
      <motion.div
        className="relative z-10 px-6 lg:px-16 pb-10 flex flex-wrap items-center gap-6 border-t border-ink-4 pt-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        {subItems.map((item) => (
          <a
            key={item.label}
            href={item.href}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noopener noreferrer" : undefined}
            className="flex items-center gap-2 text-sm text-chalk-2 hover:text-chalk transition-colors duration-200 cursor-pointer group whitespace-nowrap"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`${item.icon} text-sm`} />
            </span>
            <span className="font-mono text-xs tracking-wide group-hover:text-acid-soft transition-colors duration-200">
              {item.label}
            </span>
          </a>
        ))}

        <div className="ml-auto hidden lg:flex items-center gap-2 text-xs font-mono text-chalk-3">
          <span className="w-1.5 h-1.5 rounded-full bg-acid animate-pulse" />
          <span>Available for new engagements</span>
        </div>
      </motion.div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-ink-4" />
    </section>
  );
}
